import { type DeepMaybeRef, unwrapDeepRefs } from "@/api/common";
import { STATUS_ID, STATUS } from "@/stores/constants";
import type {
  ExportPayload,
  SignOutAllRequestPayload,
  ListVisitsQuery,
} from "./types";
import {
  useUpdateVisit,
  useUpdateVisitor,
  useExportVisits,
  useSignOutAllVisits,
  useSignOutSelectedVisits,
  useAnonymizeVisits,
} from "./mutations";
import { computed } from "vue";
import { useLocationSelector } from "@/composables/location";
import { useNotification } from "@/composables/useNotification";
import { useI18n } from "vue-i18n";
import { visitKeys } from ".";
import { useDateUtils } from "@/composables/dates";

export function useVisits(filters: DeepMaybeRef<ListVisitsQuery>) {
  const { sendErrorNotification } = useNotification();
  const { t } = useI18n();
  const { mutateAsync: updateVisitorAsync } = useUpdateVisitor();
  const { mutateAsync: updateVisitAsync } = useUpdateVisit();
  const { mutateAsync: exportVisitsAsync, isLoading: isExportLoading } =
    useExportVisits();
  const { mutateAsync: signOutAllVisitsAsync, isLoading: isSignOutAllLoading } =
    useSignOutAllVisits();
  const {
    mutateAsync: signOutSelectedVisitsAsync,
    isLoading: isSignOutSelectedLoading,
  } = useSignOutSelectedVisits();
  const { mutateAsync: anonymizeVisitsAsync, isLoading: isAnonymizeLoading } =
    useAnonymizeVisits();

  const { parseNaiveDateForRequest } = useDateUtils();
  const { locationParamZoolander } = useLocationSelector();
  const locationGroupId = computed(
    () => locationParamZoolander.value.locationGroupId
  );
  const locationId = computed(() => locationParamZoolander.value.locationId);

  const mergedFilters = computed(() => {
    const unwrappedFilters = unwrapDeepRefs(filters);
    return {
      locationId: locationId.value,
      locationGroupId: locationGroupId.value,
      expand: "host,location",
      ...unwrappedFilters,
      startedAt: parseNaiveDateForRequest(unwrappedFilters?.startedAt as Date),
      finishedAt: parseNaiveDateForRequest(
        unwrappedFilters?.finishedAt as Date
      ),
    };
  });

  const { queryKey } = visitKeys.list(mergedFilters);
  const { queryKey: countQueryKey } = visitKeys.count(mergedFilters);

  const signOutVisitor = async (pk: string) => {
    await updateVisitorAsync(
      {
        pk,
        payload: { currentStatus: STATUS_ID.OUT },
        filters: mergedFilters,
      },
      {
        onError: () =>
          sendErrorNotification(t("errors.signOutVisitFailed"), true),
      }
    );
  };

  const approveVisitor = async (pk: string) => {
    await updateVisitAsync(
      {
        pk,
        payload: { review_status: STATUS.ALLOW },
        filters: mergedFilters,
      },
      {
        onError: () =>
          sendErrorNotification(t("errors.updateVisitorFailed"), true),
      }
    );
  };

  const rejectVisitor = async (pk: string) => {
    await updateVisitAsync(
      {
        pk,
        payload: { review_status: STATUS.DENY },
        filters: mergedFilters,
      },
      {
        onError: () =>
          sendErrorNotification(t("errors.updateVisitorFailed"), true),
      }
    );
  };

  const exportVisits = (payload: ExportPayload) => {
    return exportVisitsAsync(payload, {
      onError: () =>
        sendErrorNotification(t("errors.exportVisitsFailed"), true),
    });
  };

  const signOutAllVisits = (payload: SignOutAllRequestPayload) => {
    return signOutAllVisitsAsync(
      {
        payload,
        queryKey,
        countQueryKey,
      },
      {
        onError: () =>
          sendErrorNotification(t("errors.signOutVisitsFailed"), true),
      }
    );
  };

  const signOutSelectedVisits = (payload: string[]) => {
    return signOutSelectedVisitsAsync(
      {
        payload,
        queryKey,
        countQueryKey,
      },
      {
        onError: () =>
          sendErrorNotification(
            payload.length > 1
              ? t("errors.signOutVisitsFailed")
              : t("errors.signOutVisitFailed"),
            true
          ),
      }
    );
  };

  const anonymizeVisits = (visits: string[]) => {
    return anonymizeVisitsAsync(visits, {
      onError: () =>
        sendErrorNotification(
          visits.length > 1
            ? t("errors.anonymizeVisitsFailed")
            : t("errors.anonymizeVisitFailed"),
          true
        ),
    });
  };

  return {
    signOutVisitor,
    approveVisitor,
    rejectVisitor,
    exportVisits,
    signOutAllVisits,
    signOutSelectedVisits,
    anonymizeVisits,
    isExportLoading,
    isSignOutAllLoading,
    isSignOutSelectedLoading,
    isAnonymizeLoading,
  };
}
